import type { Definition } from 'typescript-json-schema';
import { SchemaTag } from '@sap/ux-specification-types';
import type { DecoratorStep, DecoratorContext } from '../interfaces';
import { metadataKeys, type DecoratorMetadata } from '../decorators';
import { isMessageConfig } from '../decorator-paths';
import { ConditionEvaluator } from '../condition-evaluator';
import i18next from 'i18next';

/**
 * Adds a message to a schema element.
 *
 * @param element - The schema element to add the message to
 * @param root0 - Message parameters object
 * @param root0.text - The message text
 * @param root0.deletable - Whether the message is deletable (default: false)
 */
function addMessageToSchema(
    element: Definition,
    { text, deletable = false }: { text: string; deletable?: boolean }
): void {
    if (!element[SchemaTag.messages]) {
        element[SchemaTag.messages] = [];
    }
    element[SchemaTag.messages].push({ text, deletable });
}

/**
 * Message step - adds messages to properties when conditions are met.
 * Multiple @message decorators can be applied to the same property.
 */
export class Message implements DecoratorStep {
    readonly name = 'message';

    /**
     * Adds messages to properties whose conditions are met.
     *
     * @param target - The class prototype containing decorator metadata
     * @param definition - The schema definition to modify
     * @param context - Runtime context for condition evaluation
     */
    init(target: object, definition: Definition, context: DecoratorContext): void {
        if (!definition?.properties || !target) {
            return;
        }

        const evaluator = new ConditionEvaluator(context);

        for (const propertyName in definition.properties) {
            const property = definition.properties[propertyName] as Definition;
            const messageInfoList = Reflect.getMetadata(metadataKeys.message, target, propertyName);

            if (messageInfoList) {
                for (const messageInfo of messageInfoList) {
                    const { passed } = evaluator.evaluate(messageInfo);
                    if (passed && messageInfo.messageConfig) {
                        this.addConditionalMessage(messageInfo, property, evaluator);
                    }
                }
            }
        }
    }

    /**
     * Adds a translated message to the schema property if the metadata contains a MessageConfig.
     *
     * @param metadata - Decorator metadata containing the message config
     * @param definition - The schema property definition to add the message to
     * @param evaluator - Condition evaluator used to resolve PathNode params
     */
    private addConditionalMessage(
        metadata: DecoratorMetadata,
        definition: Definition,
        evaluator: ConditionEvaluator
    ): void {
        // Handle MessageConfig format (from msg() helper)
        if (metadata.messageConfig && isMessageConfig(metadata.messageConfig)) {
            const msgConfig = metadata.messageConfig;
            const resolvedParams = evaluator.resolveMessageParams(msgConfig.params);
            const messageText = i18next.t(msgConfig.i18nKey, resolvedParams);
            addMessageToSchema(definition, { text: messageText, deletable: msgConfig.deletable });
        }
    }
}
